/**
 * useLiveBlocks.ts
 *
 * Live-querying hook for all blocks in a workspace.
 * Re-renders automatically whenever any block changes in IndexedDB.
 *
 * Replaces: useBlockActions() from blockContext
 *
 * Usage:
 *   const { blocks, getBlock, getChildren, getChildrenBlocks } = useLiveBlocks(workspaceId)
 */

"use client";

import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import { appDB } from "../db/client";
import type { BlockRecord } from "../db/schema";
import type { Block } from "@/types/block";

interface UseLiveBlocksReturn {
  // All alive blocks for this workspace as a Map (same shape as old blockContext)
  blocks: Map<string, BlockRecord>;

  // Individual block lookup — same as old blockContext.getBlock()
  getBlock: (blockId: string) => BlockRecord | undefined;

  // Get child block IDs — same as old blockContext.getChildren()
  getChildren: (blockId: string) => string[];

  // Get full child block records — same as old blockContext.getChildrenBlocks()
  getChildrenBlocks: (blockId: string) => BlockRecord[];

  // Get all blocks as array — same as old blockContext.getAllBlocks()
  getAllBlocks: () => BlockRecord[];

  // Check if block exists — same as old blockContext.hasBlock()
  hasBlock: (blockId: string) => boolean;

  // Get block order for a page — same as old blockContext.getBlockOrder()
  getBlockOrder: (pageId: string) => string[];

  // Whether IDB query is still loading on first mount
  isLoading: boolean;
}

/**
 * Returns all alive blocks for a workspace, live-updating from IndexedDB.
 * Uses Dexie liveQuery — automatically re-runs when underlying data changes.
 */
export function useLiveBlocks(workspaceId: string): UseLiveBlocksReturn {
  const blockArray = useLiveQuery(
    () =>
      appDB.blocks
        .where("workspaceId")
        .equals(workspaceId)
        .and((b) => b.status !== "deleted")
        .toArray(),
    [workspaceId]
  );

  // Convert to Map for O(1) lookups — same interface as old blockContext
  const blocksMap = useMemo<Map<string, BlockRecord>>(() => {
    const map = new Map<string, BlockRecord>();
    if (!blockArray) return map;
    for (const block of blockArray) {
      map.set(block._id, block);
    }
    return map;
  }, [blockArray]);

  const getBlock = (blockId: string): BlockRecord | undefined => blocksMap.get(blockId);

  const getChildren = (blockId: string): string[] => {
    const block = blocksMap.get(blockId);
    return block?.blockIds ?? [];
  };

  const getChildrenBlocks = (blockId: string): BlockRecord[] => {
    const childIds = getChildren(blockId);
    return childIds
      .map((id) => blocksMap.get(id))
      .filter((b): b is BlockRecord => b !== undefined);
  };

  const getAllBlocks = (): BlockRecord[] => Array.from(blocksMap.values());

  const hasBlock = (blockId: string): boolean => blocksMap.has(blockId);

  const getBlockOrder = (pageId: string): string[] => {
    const page = blocksMap.get(pageId);
    return page?.blockIds ?? [];
  };

  return {
    blocks: blocksMap,
    getBlock,
    getChildren,
    getChildrenBlocks,
    getAllBlocks,
    hasBlock,
    getBlockOrder,
    isLoading: blockArray === undefined,
  };
}
